/**
 * Shared secondary-stream page generator.
 *
 * The parametric builder that turns a stream's semantics + the MOE topic tables
 * into a `LevelPageContent`. Per-stream modules (secondaryStreamG1..Ip) call this
 * with their own semantics; IB spokes are literals in secondaryStreamIb.
 */
import { levelPageIds } from './pageIds';
import {
  G1_TOPICS_BY_LEVEL,
  G2_TOPICS_BY_LEVEL,
  G3_TOPICS_BY_LEVEL,
  IP_TOPICS_BY_LEVEL,
  secondaryLevelContext,
} from './secondaryTopics';
import type { LevelPageContent, SyllabusTopic } from './types';

const secondaryBandContext: Record<string, any> = {
  lower: {
    subheadSuffix:
      'These transition years build the algebraic reasoning and confidence students need before Upper Secondary.',
    syllabusLead:
      'DeepThink aligns to MOE Lower Secondary expectations while adapting pacing and scaffolding for this stream.',
    challengeStageTitle: 'Transitioning to secondary-level demands',
    challengeStageDescription:
      'Students move from primary arithmetic into faster-paced, more abstract mathematical thinking.',
    challengeApplicationTitle: 'Applying concepts in unfamiliar contexts',
    challengeApplicationDescription:
      'Students may understand procedures but struggle when problems are presented in unfamiliar multi-step formats.',
    challengeWorkloadTitle: 'Managing consistency across subjects',
    challengeWorkloadDescription:
      'With more subjects and deadlines, students can lose momentum without focused, guided practice.',
    stageSupportTitle: 'Build understanding before speed',
    stageSupportDescription:
      'Lessons prioritise conceptual mastery and method selection, so students progress with confidence instead of guesswork.',
  },
  upper: {
    subheadSuffix:
      'These years demand consolidation, exam strategy, and consistent execution under time pressure.',
    syllabusLead:
      'DeepThink aligns to Upper Secondary outcomes and exam requirements while reinforcing essential prerequisites.',
    challengeStageTitle: 'Managing exam pressure and pacing',
    challengeStageDescription:
      'Students must balance accuracy, speed, and confidence while preparing for high-stakes assessments.',
    challengeApplicationTitle: 'Handling mixed-topic exam questions',
    challengeApplicationDescription:
      'Exam questions combine multiple concepts, so students need both technical fluency and strategic planning.',
    challengeWorkloadTitle: 'Balancing Math with full exam-year workload',
    challengeWorkloadDescription:
      'Revision competes with many subjects; without clear prioritisation, important Math topics are often under-practised.',
    stageSupportTitle: 'Train for exam execution',
    stageSupportDescription:
      'Coaching focuses on question analysis, time management, and error reduction so students perform reliably in timed papers.',
  },
};

export function buildStreamPageContent(
  secLevel: number,
  stream: any,
  streamKey: string,
  brandName: string
): LevelPageContent {
  const bandKey = secLevel <= 2 ? 'lower' : 'upper';
  const band = secondaryBandContext[bandKey];
  const streamBandContent = stream[bandKey];
  const levelCtx = secondaryLevelContext[secLevel];
  const levelLabel = `Secondary ${secLevel}`;
  const contentKey = `sec${secLevel}${stream.keySuffix}`;
  const slug = levelPageIds[contentKey];

  return {
    id: slug,
    slug: `/${slug}`,
    seo: {
      // The searcher types '<level> <stream> math syllabus' and these pages
      // already rank top-2 for it at 0% CTR — the title promised tuition while
      // the searcher wanted the MOE topic list, which the page has carried all
      // along in `syllabus.topics`. Promise what is actually there.
      title: `${levelLabel} ${stream.seoLabel} Math Syllabus & Tuition | ${brandName}`,
      description: streamBandContent.seoDescription,
    },
    hero: {
      eyebrow: `SECONDARY ${secLevel} ${stream.eyebrowLabel}`,
      headline: streamBandContent.headline,
      subhead: `${levelLabel} ${stream.streamName} focuses on ${streamBandContent.subheadFocus} ${
        streamKey === 'ip' ? levelCtx.ipHeroSuffix : levelCtx.heroSuffix
      }`,
      ctaLabel: 'Book a free trial class',
    },
    syllabus: (() => {
      const baseSyllabus = {
        title: 'What we cover',
        eyebrow: 'Syllabus',
        body: `${band.syllabusLead} ${streamBandContent.syllabusBody}`,
        note: streamBandContent.syllabusNote,
      };
      const g1TopicsForLevel =
        streamKey === 'g1' && G1_TOPICS_BY_LEVEL[secLevel];
      if (g1TopicsForLevel) {
        const isStringList = typeof g1TopicsForLevel[0] === 'string';
        return {
          ...baseSyllabus,
          topics: isStringList
            ? [
                {
                  category: 'Topics covered',
                  items: g1TopicsForLevel as string[],
                },
              ]
            : (g1TopicsForLevel as SyllabusTopic[]),
        };
      }
      const g2TopicsForLevel =
        streamKey === 'g2' && G2_TOPICS_BY_LEVEL[secLevel];
      if (g2TopicsForLevel) {
        return {
          ...baseSyllabus,
          topics: g2TopicsForLevel,
        };
      }
      const g3TopicsForLevel =
        streamKey === 'g3' && G3_TOPICS_BY_LEVEL[secLevel];
      if (g3TopicsForLevel) {
        return {
          ...baseSyllabus,
          topics: g3TopicsForLevel,
        };
      }
      const ipTopicsForLevel =
        streamKey === 'ip' && IP_TOPICS_BY_LEVEL[secLevel];
      if (ipTopicsForLevel) {
        return {
          ...baseSyllabus,
          topics: ipTopicsForLevel,
        };
      }
      return {
        ...baseSyllabus,
        topics: streamBandContent.syllabusTopics,
      };
    })(),
    challenges: {
      title: 'Common challenges at this level',
      body: `${levelLabel} students in the ${stream.streamName} often face:`,
      items: [
        {
          title: levelCtx.challengeTitle,
          description: levelCtx.challengeDescription,
        },
        {
          title: streamBandContent.challengeFocusTitle,
          description: streamBandContent.challengeFocusDescription,
        },
        {
          title: band.challengeApplicationTitle,
          description: band.challengeApplicationDescription,
        },
        {
          title: band.challengeWorkloadTitle,
          description: band.challengeWorkloadDescription,
        },
      ],
    },
    howWeHelp: {
      title: `How DeepThink helps Sec ${secLevel} ${stream.seoLabel} students`,
      body: `DeepThink supports ${levelLabel} students in the ${stream.streamName} through targeted diagnostics, structured teaching, and strategic practice:`,
      points: [
        {
          title: 'Diagnose and close gaps quickly',
          description:
            'Our platform identifies chapter-level weaknesses early so students focus effort where it matters most.',
        },
        {
          title: levelCtx.supportTitle,
          description: levelCtx.supportDescription,
        },
        {
          title: streamBandContent.streamSupportTitle,
          description: streamBandContent.streamSupportDescription,
        },
        {
          title: 'Track progress with clear next steps',
          description:
            'Students and parents see what is secure, what needs reinforcement, and which topics should be prioritised next.',
        },
      ],
      closingLine: `With consistent support, students can ${streamBandContent.progressionGoal}`,
    },
    ctaSection: {
      title: 'Start with a trial class',
      body: `See how DeepThink helps Sec ${secLevel} ${stream.seoLabel} students build momentum through clear, structured support.`,
      ctaLabel: 'Book your free trial',
    },
  };
}
